import { useRef, useEffect, useState } from 'react';
import { Star } from 'lucide-react';

export const TestimonialsSection = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const testimonials = [
    {
      id: 1,
      text: "I purchased a home that needed a full rehab in Fort Worth. Hired C.A.B as a consultant foreman for the project. Their team has many years in construction experience and it shows. The team is knowledgeable, diligent, provides great advice and takes a problem solving approach to adverse items that come up during the project. C.A.B's networks and knowledge helped a very intense project flow smoothly. Highly recommend for your next renovation or new construction project.",
      author: "Fort Worth Client",
      location: "Fort Worth, TX",
      rating: 5,
    },
  ];

  // Auto-scroll effect
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused) return;

    let animationId;
    let scrollSpeed = 0.5;

    const scroll = () => {
      if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth / 2) {
        scrollContainer.scrollLeft = 0;
      }
      scrollContainer.scrollLeft += scrollSpeed;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);

  return (
    <section data-testid="testimonials-section" className="py-24 bg-navy grain-overlay overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-copper text-xs font-inter uppercase tracking-widest">
            Testimonials
          </span>
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mt-4">
            Hear from our clients
          </h2>
          <p className="text-white/60 font-inter mt-4">
            Hear from our happy clients about their experience working with C.A.B and the quality of our craftsmanship.
          </p>
        </div>
      </div>

      {/* Testimonials Carousel */}
      <div 
        ref={scrollRef}
        className="flex overflow-x-hidden space-x-6 py-8"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        data-testid="testimonials-carousel"
      >
        {/* Double the testimonials for infinite scroll effect */}
        {[...testimonials, ...testimonials].map((testimonial, index) => (
          <div
            key={`${testimonial.id}-${index}`}
            className="flex-shrink-0 w-[400px] bg-white/5 backdrop-blur-sm border border-white/10 p-8"
            data-testid={`testimonial-card-${index}`}
          >
            <div className="flex items-center space-x-1 mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-copper text-copper" />
              ))}
            </div>
            <p className="text-white/90 font-inter leading-relaxed mb-6">
              "{testimonial.text}"
            </p>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-copper/20 rounded-full flex items-center justify-center">
                <span className="text-copper font-playfair font-bold text-sm">
                  {testimonial.author.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <p className="text-white font-medium font-inter text-sm">{testimonial.author}</p>
                <p className="text-white/50 text-xs font-inter">{testimonial.location}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Customer Reviews */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex items-center justify-center space-x-4">
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-copper text-copper" />
            ))}
          </div>
          <span className="text-white font-inter">
            5-Star Customer Reviews
          </span>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
